import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTournamentContext } from "../context/TournamentContext";
import {
  createTournament,
  updateTournament,
  uploadTournamentFile,
} from "../api/tournamentApi";
import { useNavigate, useLocation } from "react-router-dom";

const TournamentForm = () => {
  const [name, setName] = useState("");
  const [status, setStatus] = useState("Scheduled");
  const [startTime, setStartTime] = useState(new Date());
  const [description, setDescription] = useState("");
  const [participationFeeCurrency, setParticipationFeeCurrency] =
    useState("INR");
  const [tournamentDuration, setTournamentDuration] = useState("50");
  const [format, setFormat] = useState("Knockout");
  const [triggerTime, setTriggerTime] = useState(new Date()); // Same as startTime initially
  const [participationFee, setParticipationFee] = useState(0);
  const [minPrizeMoney, setMinPrizeMoney] = useState(0);
  const [tnc, setTnc] = useState("");
  const [matchDuration, setMatchDuration] = useState(5);
  const [minParticipants, setMinParticipants] = useState(2);
  const [maxParticipants, setMaxParticipants] = useState(8);
  const [numberOfRounds, setNumberOfRounds] = useState(1);
  const [error, setError] = useState("");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);
  const { setTournaments } = useTournamentContext();
  const navigate = useNavigate();
  const location = useLocation();

  const tournamentData = location.state?.tournament;

  useEffect(() => {
    if (tournamentData) {
      setName(tournamentData.name || "");
      setStatus(tournamentData.status || "Scheduled");
      setStartTime(new Date(tournamentData.start_time));
      setTriggerTime(new Date(tournamentData.trigger_time)); // Initializing with tournament data if available
      setDescription(tournamentData.description || "");
      setParticipationFeeCurrency(
        tournamentData.participation_fee_currency || "INR"
      );
      setTournamentDuration(tournamentData.tournament_duration || "");
      setFormat(tournamentData.format || "");
      setParticipationFee(tournamentData.participation_fee || 0);
      setMinPrizeMoney(tournamentData.min_prize_money || 0);
      setTnc(tournamentData.tnc || "");
      setMatchDuration(tournamentData.match_duration || 0);
      setMinParticipants(tournamentData.min_participants || 2);
      setMaxParticipants(tournamentData.max_participants || 100);
      setNumberOfRounds(tournamentData.number_of_rounds || 1);
    }
  }, [tournamentData]);

  const handleStartTimeChange = (newStartTime) => {
    setStartTime(newStartTime);
    setTriggerTime(newStartTime); // Automatically set triggerTime to match startTime
  };

  const handleBackClick = () => {
    navigate("/dashboard");
  };

  const getTournamentDuration = () => {
    const value = Math.log2(Math.pow(2, Math.ceil(Math.log2(maxParticipants))));
    return 2 * (value * matchDuration) + 2 * (value - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isFormInvalid) {
      setError("Please fix the highlighted fields before submitting.");
      return;
    }

    const token = localStorage.getItem("accessToken");

    try {
      const tDuration = getTournamentDuration();
      //  setTournamentDuration( getTournamentDuration());
      const tournamentDataToSubmit = {
        name,
        status,
        startTime,
        description,
        participationFeeCurrency,
        tournamentDuration: tDuration,
        format,
        triggerTime,
        participationFee,
        minPrizeMoney,
        tnc,
        matchDuration,
        minParticipants,
        maxParticipants,
        numberOfRounds: Math.log2(
          Math.pow(2, Math.ceil(Math.log2(maxParticipants)))
        ),
      };

      if (tournamentData) {
        // Update tournament logic
        await updateTournament(
          tournamentDataToSubmit,
          tournamentData.id,
          token
        );
        setTournaments((prev) =>
          prev.map((t) =>
            t.id === tournamentData.id ? { ...t, ...tournamentDataToSubmit } : t
          )
        );
        alert("Tournament updated successfully");
        navigate("/fetch-tournaments");
      } else {
        // Create new tournament logic
        const createdTournament = await createTournament(
          tournamentDataToSubmit,
          token
        );
        setTournaments((prev) => [...prev, createdTournament]);
        alert("Tournament created successfully");
        // Reset form only after creation, not after update
        resetForm();
        navigate("/fetch-tournaments");
      }
    } catch (error) {
      console.error("Error creating or updating tournament:", error);
      setError("Error creating/updating tournament. Please try again.");
    }
  };

  const handleUploadClick = async (e) => {
    try {
      const file = e.target.files[0];

      if (!file) {
        alert("Please select a file to upload");
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("File size should not exceed 5MB");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      const result = await uploadTournamentFile(formData);

      if (result.status) {
        alert("File uploaded successfully");
        navigate("/fetch-tournaments");
      } else {
        // Handle specific error messages from the server
        const errorMessage =
          result.message ||
          "Failed to upload tournament file. Please try again.";
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Error uploading tournament file:", error);
      alert(
        "An unexpected error occurred while uploading the file. Please try again."
      );
    } finally {
      e.target.value = "";
    }
  };

  // Reset form function after creation
  const resetForm = () => {
    setName("");
    setStatus("Scheduled");
    setStartTime(new Date());
    setTriggerTime(new Date());
    setDescription("");
    setParticipationFeeCurrency("INR");
    setTournamentDuration("");
    setFormat("");
    setParticipationFee(0);
    setMinPrizeMoney(0);
    setTnc("");
    setMatchDuration(0);
    setMinParticipants(2);
    setMaxParticipants(100);
    setNumberOfRounds(1);
  };

  const minDateTime = new Date(now.getTime() + 3 * 60000); // Start time must be at least 3 minutes from now
  const isHidden = true;

  const isPowerOfTwo = (n) =>
    Number.isInteger(n) && n >= 2 && (n & (n - 1)) === 0;
  const isOnlyWhitespace = (s) =>
    typeof s === "string" && s.length > 0 && s.trim().length === 0;

  // Field validations
  const startTimeError =
    !startTime ||
    isNaN(new Date(startTime).getTime()) ||
    new Date(startTime).getTime() < minDateTime.getTime();
  const matchDurationError =
    !Number.isInteger(matchDuration) || matchDuration < 1;
  const participationFeeError =
    isNaN(participationFee) || participationFee < 0;
  const minPrizeMoneyError = isNaN(minPrizeMoney) || minPrizeMoney < 0;
  const maxParticipantsError = !isPowerOfTwo(maxParticipants);
  const formatError = !format || format.trim().length === 0;
  const descriptionError = isOnlyWhitespace(description);
  const tncError = isOnlyWhitespace(tnc);
  const isFormInvalid =
    startTimeError ||
    matchDurationError ||
    participationFeeError ||
    minPrizeMoneyError ||
    maxParticipantsError ||
    formatError ||
    descriptionError ||
    tncError;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={3} sx={{ padding: 4 }}>
        <h2>Create Tournament</h2>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBackClick}
            sx={{ marginBottom: 2 }}
          >
            Back to Dashboard
          </Button>
          <input
            type="file"
            accept=".csv"
            id="csv-upload"
            style={{ display: "none" }}
            onChange={handleUploadClick} // Update this function to handle file upload
          />
          <label htmlFor="csv-upload">
            <Button variant="outlined" color="primary" component="span">
              Upload Tournament Sheet
            </Button>
          </label>
        </div>
        <form onSubmit={handleSubmit}>
          {/* <TextField
            fullWidth
            label="Tournament Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            sx={{ marginBottom: 2 }}
            
          /> */}
          <TextField
            fullWidth
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            required
            disabled
            sx={{ marginBottom: 2 }}
          />
          <Box sx={{ marginBottom: 2 }}>
            <DateTimePicker
              label="Start Time"
              value={startTime}
              onChange={handleStartTimeChange}
              minDateTime={minDateTime}
              disablePast
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: startTimeError,
                  helperText: startTimeError
                    ? `Please choose a time at least 3 minutes from now (>= ${minDateTime.toLocaleTimeString(
                      [],
                      { hour: "2-digit", minute: "2-digit" }
                    )}).`
                    : "",
                },
              }}
            />
          </Box>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={descriptionError}
            helperText={
              descriptionError ? "Description cannot be only spaces." : ""
            }
            sx={{ marginBottom: 2 }}
          />
          <FormControl fullWidth sx={{ marginBottom: 2 }} required>
            <InputLabel id="currency-select-label">
              Participation Fee Currency
            </InputLabel>
            <Select
              labelId="currency-select-label"
              value={participationFeeCurrency}
              onChange={(e) => setParticipationFeeCurrency(e.target.value)}
              label="Participation Fee Currency"
            >
              <MenuItem value="USD">USD - US Dollar</MenuItem>
              <MenuItem value="INR">INR - Indian Rupee</MenuItem>
            </Select>
            {!participationFeeCurrency && (
              <FormHelperText error>Currency is required.</FormHelperText>
            )}
          </FormControl>
          <TextField
            fullWidth
            label="Match Duration (minutes)"
            type="number"
            value={matchDuration}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => setMatchDuration(parseInt(e.target.value, 10))}
            required
            inputProps={{ min: 1, step: 1 }}
            error={matchDurationError}
            helperText={
              matchDurationError
                ? "Match duration must be a whole number and at least 1."
                : ""
            }
            sx={{ marginBottom: 2 }}
          />
          {/* <TextField
            fullWidth
            label="Tournament Duration"
            type='number'
            value={tournamentDuration}
            onChange={(e) => setTournamentDuration(e.target.value)}
            required
            sx={{ marginBottom: 2 }}
          /> */}
          <TextField
            fullWidth
            label="Format"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            required
            error={formatError}
            helperText={
              formatError ? "Format is required and cannot be only spaces." : ""
            }
            sx={{ marginBottom: 2 }}
          />
          {!isHidden && (
            <Box sx={{ marginBottom: 2 }}>
              <DateTimePicker
                label="Trigger Time"
                value={triggerTime}
                onChange={() => { }} // Disable this field by not handling changes
                disabled // Disable the field so users can't change it
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
          )}
          <TextField
            fullWidth
            label="Participation Fee"
            type="number"
            value={participationFee}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => setParticipationFee(parseFloat(e.target.value))}
            required
            inputProps={{ min: 0 }}
            error={participationFeeError}
            helperText={
              participationFeeError
                ? "Participation fee cannot be negative."
                : ""
            }
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Minimum Prize Money"
            type="number"
            value={minPrizeMoney}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => setMinPrizeMoney(parseFloat(e.target.value))}
            required
            inputProps={{ min: 0 }}
            error={minPrizeMoneyError}
            helperText={
              minPrizeMoneyError ? "Prize money cannot be negative." : ""
            }
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Terms and Conditions"
            value={tnc}
            onChange={(e) => setTnc(e.target.value)}
            error={tncError}
            helperText={
              tncError ? "Terms and conditions cannot be only spaces." : ""
            }
            sx={{ marginBottom: 2 }}
          />

          {/* <TextField
            fullWidth
            label="Minimum Participants"
            type="number"
            value={minParticipants}
            onChange={(e) => setMinParticipants(parseInt(e.target.value, 10))}
            required
            sx={{ marginBottom: 2 }}
          /> */}
          <TextField
            fullWidth
            label="Maximum Participants"
            type="number"
            value={maxParticipants}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => setMaxParticipants(parseInt(e.target.value, 10))}
            required
            inputProps={{ min: 2, step: 1 }}
            error={maxParticipantsError}
            helperText={
              maxParticipantsError
                ? "Maximum participants must be a power of 2 (2, 4, 8, 16, 32, ...)."
                : ""
            }
            sx={{ marginBottom: 2 }}
          />
          {/* <TextField
            fullWidth
            label="Number of Rounds"
            type="number"
            value={numberOfRounds}
            onChange={(e) => setNumberOfRounds(parseInt(e.target.value, 10))}
            required
            sx={{ marginBottom: 2 }}
          /> */}
          {error && <p style={{ color: "red" }}>{error}</p>}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ marginTop: 2 }}
          >
            {tournamentData ? "Save Tournament" : "Create Tournament"}
          </Button>
        </form>
      </Paper>
    </LocalizationProvider>
  );
};

export default TournamentForm;
